import React, { useState, useMemo } from 'react';
import {
  Users,
  UserPlus,
  UserCheck,
  CreditCard,
  DollarSign,
  TrendingUp,
  CalendarCheck,
  CalendarX,
  Film,
  Eye,
  Heart,
  Users2,
  HeadphonesIcon,
  AlertTriangle,
  Clock,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import { useDashboardAnalytics } from '../hooks/useAnalytics';
import { AnalyticsDateRange, TrendPoint, RevenueTrendPoint } from '../types/analytics';
import {
  cn,
  formatCents,
  formatNumber,
  formatCompactNumber,
  formatPercent,
} from '../utils/helpers';

// ============================================================================
// Date range presets
// ============================================================================

type DatePreset = '7d' | '30d' | '90d' | 'all';

const presets: { label: string; value: DatePreset }[] = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'All time', value: 'all' },
];

function getDateRange(preset: DatePreset): AnalyticsDateRange | undefined {
  if (preset === 'all') return undefined;
  const end = new Date();
  const start = new Date();
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

// ============================================================================
// Stat Card
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  loading,
  subtitle,
}) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground">{value}</p>
        )}
        {subtitle && !loading && (
          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Mini bar chart for trend data
// ============================================================================

interface MiniTrendChartProps {
  data: TrendPoint[] | RevenueTrendPoint[];
  valueKey?: 'count' | 'amountCents';
  color?: string;
  loading?: boolean;
}

const MiniTrendChart: React.FC<MiniTrendChartProps> = ({
  data,
  valueKey = 'count',
  color = 'bg-primary',
  loading,
}) => {
  if (loading) {
    return (
      <div className="flex items-end gap-0.5 h-16">
        {Array.from({ length: 14 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-muted/20 rounded-t animate-pulse"
            style={{ height: `${20 + Math.random() * 60}%` }}
          />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-16 text-xs text-muted-foreground">
        No trend data
      </div>
    );
  }

  const values = data.map((d) => (valueKey === 'amountCents' ? (d as RevenueTrendPoint).amountCents : (d as TrendPoint).count));
  const max = Math.max(...values, 1);

  return (
    <div className="flex items-end gap-0.5 h-16">
      {values.map((v, i) => (
        <div
          key={i}
          className={cn('flex-1 rounded-t transition-all', color, 'opacity-70 hover:opacity-100')}
          style={{ height: `${Math.max((v / max) * 100, 4)}%` }}
          title={`${data[i].date}: ${valueKey === 'amountCents' ? formatCents(v) : formatNumber(v)}`}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Status breakdown bars
// ============================================================================

interface StatusBreakdownProps {
  data: Record<string, number> | undefined;
  loading?: boolean;
  colorMap?: Record<string, string>;
}

const defaultColors: Record<string, string> = {
  COMPLETED: 'bg-emerald-500',
  SUCCEEDED: 'bg-emerald-500',
  APPROVED: 'bg-emerald-500',
  READY: 'bg-emerald-500',
  ACTIVE: 'bg-emerald-500',
  CONFIRMED: 'bg-blue-500',
  IN_PROGRESS: 'bg-blue-500',
  UPCOMING: 'bg-blue-500',
  OPEN: 'bg-yellow-500',
  PENDING: 'bg-yellow-500',
  PENDING_REVIEW: 'bg-yellow-500',
  PROCESSING: 'bg-blue-400',
  CANCELLED: 'bg-orange-500',
  FAILED: 'bg-red-500',
  REJECTED: 'bg-red-500',
  REFUNDED: 'bg-orange-400',
  NO_SHOW: 'bg-red-400',
  CLOSED: 'bg-muted-foreground',
  RESOLVED: 'bg-emerald-400',
};

const StatusBreakdown: React.FC<StatusBreakdownProps> = ({ data, loading, colorMap }) => {
  if (loading || !data) {
    return <div className="h-20 bg-muted/20 rounded animate-pulse" />;
  }

  const entries = Object.entries(data).filter(([, v]) => v > 0);
  const total = entries.reduce((sum, [, v]) => sum + v, 0);
  if (total === 0) return <p className="text-xs text-muted-foreground">No data</p>;

  const colors = { ...defaultColors, ...colorMap };

  return (
    <div className="space-y-2">
      {/* Stacked bar */}
      <div className="flex h-3 rounded-full overflow-hidden bg-muted/20">
        {entries.map(([key, val]) => (
          <div
            key={key}
            className={cn('transition-all', colors[key] || 'bg-muted-foreground')}
            style={{ width: `${(val / total) * 100}%` }}
            title={`${key}: ${formatNumber(val)}`}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {entries.map(([key, val]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className={cn('w-2 h-2 rounded-full', colors[key] || 'bg-muted-foreground')} />
            <span className="text-[10px] text-muted-foreground">
              {key.replace(/_/g, ' ')}
            </span>
            <span className="text-[10px] font-medium text-foreground">{formatNumber(val)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Section wrapper
// ============================================================================

interface SectionProps {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  error?: string | null;
}

const Section: React.FC<SectionProps> = ({ title, icon: Icon, children, error }) => (
  <section className="space-y-4">
    <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
      <Icon className="h-4 w-4 text-primary" />
      {title}
    </h2>
    {error ? (
      <div className="card p-4 border-destructive/30">
        <p className="text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </p>
      </div>
    ) : (
      children
    )}
  </section>
);

// ============================================================================
// Dashboard Page
// ============================================================================

const Dashboard: React.FC = () => {
  const [preset, setPreset] = useState<DatePreset>('30d');
  const dateRange = useMemo(() => getDateRange(preset), [preset]);

  const {
    users,
    bookings,
    payments,
    content,
    groupSessions,
    support,
    loading: anyLoading,
    refetchAll,
  } = useDashboardAnalytics(dateRange);

  return (
    <div className="p-6 space-y-8 overflow-y-auto h-full scrollbar-thin">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-0.5">App analytics and user insights</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date range selector */}
          <div className="relative">
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as DatePreset)}
              className={cn(
                'appearance-none pl-3 pr-8 py-2 rounded-lg border border-border bg-card',
                'text-sm text-foreground cursor-pointer',
                'focus:outline-none focus:ring-2 focus:ring-primary/50'
              )}
            >
              {presets.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>
          <button
            onClick={refetchAll}
            disabled={anyLoading}
            className="p-2 rounded-lg border border-border hover:bg-muted/30 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4 text-muted-foreground', anyLoading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* ================================================================== */}
      {/* USERS */}
      {/* ================================================================== */}
      <Section title="Users" icon={Users} error={users.error}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total Users" value={users.data ? formatNumber(users.data.totalUsers) : '—'} icon={Users} color="text-primary" bgColor="bg-primary/10" loading={users.loading} />
          <StatCard label="New (period)" value={users.data ? formatNumber(users.data.newUsersInPeriod) : '—'} icon={UserPlus} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={users.loading} />
          <StatCard label="Active (period)" value={users.data ? formatNumber(users.data.activeUsersInPeriod) : '—'} icon={TrendingUp} color="text-blue-400" bgColor="bg-blue-400/10" loading={users.loading} />
          <StatCard label="Verified" value={users.data ? formatNumber(users.data.verifiedUsers) : '—'} icon={UserCheck} color="text-cyan-400" bgColor="bg-cyan-400/10" loading={users.loading} />
          <StatCard label="Providers" value={users.data ? formatNumber(users.data.providersCount) : '—'} icon={Users2} color="text-orange-400" bgColor="bg-orange-400/10" loading={users.loading} />
          <StatCard label="Consumers" value={users.data ? formatNumber(users.data.consumersOnlyCount) : '—'} icon={Users} color="text-purple-400" bgColor="bg-purple-400/10" loading={users.loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* User growth trend */}
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">User Growth Trend</p>
            <MiniTrendChart data={users.data?.userGrowthTrend || []} loading={users.loading} color="bg-primary" />
          </div>
          {/* Provider onboarding */}
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Provider Onboarding</p>
            {users.loading ? (
              <div className="h-16 bg-muted/20 rounded animate-pulse" />
            ) : users.data ? (
              <div className="space-y-2">
                {([
                  ['Stripe Connected', users.data.providerOnboarding?.stripeConnected ?? 0, users.data.providersCount],
                  ['Charges Enabled', users.data.providerOnboarding?.chargesEnabled ?? 0, users.data.providersCount],
                  ['Payouts Enabled', users.data.providerOnboarding?.payoutsEnabled ?? 0, users.data.providersCount],
                ] as [string, number, number][]).map(([label, val, total]) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-32 flex-shrink-0">{label}</span>
                    <div className="flex-1 h-2 rounded-full bg-muted/20 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: total > 0 ? `${(val / total) * 100}%` : '0%' }}
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground w-12 text-right">{formatNumber(val)}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </Section>

      {/* ================================================================== */}
      {/* BOOKINGS */}
      {/* ================================================================== */}
      <Section title="Bookings" icon={CalendarCheck} error={bookings.error}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Total Bookings" value={bookings.data ? formatNumber(bookings.data.totalBookings) : '—'} icon={CalendarCheck} color="text-primary" bgColor="bg-primary/10" loading={bookings.loading} />
          <StatCard label="Completion Rate" value={bookings.data ? formatPercent(bookings.data.completionRate) : '—'} icon={TrendingUp} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={bookings.loading} />
          <StatCard label="Cancellation" value={bookings.data ? formatPercent(bookings.data.cancellationRate) : '—'} icon={CalendarX} color="text-orange-400" bgColor="bg-orange-400/10" loading={bookings.loading} />
          <StatCard label="Avg. Value" value={bookings.data ? formatCents(bookings.data.averageBookingValueCents) : '—'} icon={DollarSign} color="text-blue-400" bgColor="bg-blue-400/10" loading={bookings.loading} />
          <StatCard label="Total Revenue" value={bookings.data ? formatCents(bookings.data.totalRevenueCents) : '—'} icon={DollarSign} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={bookings.loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Bookings by Status</p>
            <StatusBreakdown data={bookings.data?.bookingsByStatus} loading={bookings.loading} />
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Booking Trend</p>
            <MiniTrendChart data={bookings.data?.bookingTrend || []} loading={bookings.loading} color="bg-blue-500" />
          </div>
        </div>

        {/* Top services */}
        {bookings.data && bookings.data.topServices && bookings.data.topServices.length > 0 && (
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Top Services</p>
            <div className="space-y-2">
              {bookings.data.topServices.slice(0, 5).map((svc, i) => {
                const maxCount = bookings.data!.topServices?.[0]?.count || 1;
                return (
                  <div key={svc.serviceId} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}.</span>
                    <span className="text-xs text-foreground flex-1 truncate">{svc.serviceName}</span>
                    <div className="w-24 h-2 rounded-full bg-muted/20 overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${(svc.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-foreground w-10 text-right">{formatNumber(svc.count)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Section>

      {/* ================================================================== */}
      {/* PAYMENTS */}
      {/* ================================================================== */}
      <Section title="Payments & Revenue" icon={DollarSign} error={payments.error}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Total Revenue" value={payments.data ? formatCents(payments.data.totalRevenueCents) : '—'} icon={DollarSign} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={payments.loading} />
          <StatCard label="Platform Fees" value={payments.data ? formatCents(payments.data.platformFeesCents) : '—'} icon={CreditCard} color="text-primary" bgColor="bg-primary/10" loading={payments.loading} />
          <StatCard label="Provider Payouts" value={payments.data ? formatCents(payments.data.providerPayoutsCents) : '—'} icon={DollarSign} color="text-blue-400" bgColor="bg-blue-400/10" loading={payments.loading} />
          <StatCard label="Success Rate" value={payments.data ? formatPercent(payments.data.paymentSuccessRate) : '—'} icon={TrendingUp} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={payments.loading} />
          <StatCard
            label="Refunds"
            value={payments.data ? formatNumber(payments.data.refundStats?.totalRefunds) : '—'}
            icon={CreditCard}
            color="text-orange-400"
            bgColor="bg-orange-400/10"
            loading={payments.loading}
            subtitle={payments.data ? formatCents(payments.data.refundStats?.totalRefundedCents) : undefined}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Payment Status</p>
            <StatusBreakdown data={payments.data?.paymentsByStatus} loading={payments.loading} />
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Revenue Trend</p>
            <MiniTrendChart data={payments.data?.revenueTrend || []} valueKey="amountCents" loading={payments.loading} color="bg-emerald-500" />
          </div>
        </div>
      </Section>

      {/* ================================================================== */}
      {/* CONTENT */}
      {/* ================================================================== */}
      <Section title="Content" icon={Film} error={content.error}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="New (period)" value={content.data ? formatNumber(content.data.newContentInPeriod) : '—'} icon={Film} color="text-primary" bgColor="bg-primary/10" loading={content.loading} />
          <StatCard label="Total Views" value={content.data ? formatCompactNumber(content.data.totalViews) : '—'} icon={Eye} color="text-blue-400" bgColor="bg-blue-400/10" loading={content.loading} />
          <StatCard label="Total Likes" value={content.data ? formatCompactNumber(content.data.totalLikes) : '—'} icon={Heart} color="text-red-400" bgColor="bg-red-400/10" loading={content.loading} />
          <StatCard label="Avg. Views" value={content.data ? formatNumber(content.data.averageViewsPerContent) : '—'} icon={Eye} color="text-cyan-400" bgColor="bg-cyan-400/10" loading={content.loading} />
          <StatCard label="Pending Review" value={content.data?.byStatus?.PENDING_REVIEW != null ? formatNumber(content.data.byStatus.PENDING_REVIEW) : '—'} icon={Clock} color="text-yellow-400" bgColor="bg-yellow-400/10" loading={content.loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Content by Status</p>
            <StatusBreakdown data={content.data?.byStatus} loading={content.loading} />
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Content by Source</p>
            <StatusBreakdown
              data={content.data?.contentBySource}
              loading={content.loading}
              colorMap={{ UPLOAD: 'bg-primary', TIKTOK: 'bg-pink-500', INSTAGRAM: 'bg-purple-500', SORA: 'bg-cyan-500' }}
            />
          </div>
        </div>
      </Section>

      {/* ================================================================== */}
      {/* GROUP SESSIONS */}
      {/* ================================================================== */}
      <Section title="Group Sessions" icon={Users2} error={groupSessions.error}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Total Sessions" value={groupSessions.data ? formatNumber(groupSessions.data.totalSessions) : '—'} icon={Users2} color="text-primary" bgColor="bg-primary/10" loading={groupSessions.loading} />
          <StatCard label="Participants" value={groupSessions.data ? formatNumber(groupSessions.data.totalParticipants) : '—'} icon={Users} color="text-blue-400" bgColor="bg-blue-400/10" loading={groupSessions.loading} />
          <StatCard label="Fill Rate" value={groupSessions.data ? formatPercent(groupSessions.data.averageFillRate) : '—'} icon={TrendingUp} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={groupSessions.loading} />
          <StatCard label="Revenue" value={groupSessions.data ? formatCents(groupSessions.data.revenueCents) : '—'} icon={DollarSign} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={groupSessions.loading} />
          <StatCard label="Free Sessions" value={groupSessions.data ? formatNumber(groupSessions.data.freeSessionsCount) : '—'} icon={CalendarCheck} color="text-purple-400" bgColor="bg-purple-400/10" loading={groupSessions.loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Sessions by Status</p>
            <StatusBreakdown data={groupSessions.data?.sessionsByStatus} loading={groupSessions.loading} />
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Session Trend</p>
            <MiniTrendChart data={groupSessions.data?.sessionTrend || []} loading={groupSessions.loading} color="bg-purple-500" />
          </div>
        </div>
      </Section>

      {/* ================================================================== */}
      {/* SUPPORT */}
      {/* ================================================================== */}
      <Section title="Support" icon={HeadphonesIcon} error={support.error}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Total Tickets" value={support.data ? formatNumber(support.data.totalTickets) : '—'} icon={HeadphonesIcon} color="text-primary" bgColor="bg-primary/10" loading={support.loading} />
          <StatCard label="Open" value={support.data ? formatNumber(support.data.openTicketsCount) : '—'} icon={AlertTriangle} color="text-yellow-400" bgColor="bg-yellow-400/10" loading={support.loading} />
          <StatCard label="Avg. Resolution" value={support.data?.averageResolutionTimeHours != null ? `${support.data.averageResolutionTimeHours.toFixed(1)}h` : '—'} icon={Clock} color="text-blue-400" bgColor="bg-blue-400/10" loading={support.loading} />
          <StatCard label="Escalated" value={support.data ? formatNumber(support.data.escalationStats?.totalEscalated) : '—'} icon={AlertTriangle} color="text-red-400" bgColor="bg-red-400/10" loading={support.loading} />
          <StatCard label="Escalation Rate" value={support.data ? formatPercent(support.data.escalationStats?.escalationRate) : '—'} icon={TrendingUp} color="text-orange-400" bgColor="bg-orange-400/10" loading={support.loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Tickets by Status</p>
            <StatusBreakdown data={support.data?.ticketsByStatus} loading={support.loading} />
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">By Priority</p>
            <StatusBreakdown
              data={support.data?.ticketsByPriority}
              loading={support.loading}
              colorMap={{ LOW: 'bg-emerald-500', MEDIUM: 'bg-yellow-500', HIGH: 'bg-orange-500', URGENT: 'bg-red-500' }}
            />
          </div>
          <div className="card p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Ticket Trend</p>
            <MiniTrendChart data={support.data?.ticketTrend || []} loading={support.loading} color="bg-orange-500" />
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Dashboard;
